import { useState, useEffect, useMemo } from "react"
import {
  Search,
  Calculator,
  FileText,
  Users,
  CreditCard,
  TrendingUp,
  Lightbulb,
  Filter,
  DollarSign,
  Building2,
  X,
  ChevronDown,
  Package,
  ShoppingCart,
  Receipt,
} from "lucide-react"
import { Button } from "../components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover"
import { Badge } from "../components/ui/badge"
import { useKeyboardNavigation } from "./keyboard-navigation-provider"
import { TranslatedText } from "./translated-text"
import { useTranslation } from "../hooks/useTranslation"
import { useQuery } from "@tanstack/react-query"
import { apiService } from "../lib/api"
import { useNavigate } from "react-router-dom"
import { getCompanyId } from "../lib/config"

// Quick Actions are static and always available

interface SearchFilters {
  category: string[]
  dateRange: string
  amountRange: string
  client: string[]
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState("")
  const [query, setQuery] = useState("")
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [savedFilters, setSavedFilters] = useState<Array<{ name: string; filters: SearchFilters }>>([])
  const [activeFilters, setActiveFilters] = useState<SearchFilters>({
    category: [],
    dateRange: "",
    amountRange: "",
    client: [],
  })
  const [showFilters, setShowFilters] = useState(false)
  const { setFocusedElement } = useKeyboardNavigation()
  const { tSync } = useTranslation()
  const navigate = useNavigate()

  // Safely get companyId - don't throw error for guest users
  let companyId: string | null = null
  try {
    companyId = getCompanyId()
  } catch (error) {
    // Guest users don't have a company ID yet
    companyId = null
  }

  const searchPlaceholder = tSync("Search transactions, clients, reports...", "Search transactions, clients, reports...")

  // Fetch search results from API
  const { data: apiSearchResults, isLoading: isSearching } = useQuery({
    queryKey: ['globalSearch', query, companyId],
    queryFn: async () => {
      if (!query.trim()) return null
      return await apiService.globalSearch({
        q: query,
        companyId: companyId || undefined,
        limit: 10
      })
    },
    enabled: query.trim().length > 0,
    staleTime: 30000, // Cache for 30 seconds
  })

  // Transform API results into search results format
  const searchResults = useMemo(() => {
    // Quick Actions (always available)
    const quickActions = [
      {
        id: "hotel",
        icon: Building2,
        label: "Hotel Management",
        category: "Quick Actions",
        shortcut: "Ctrl+H",
        action: "hotel",
        type: "action",
      },
      {
        id: "pos",
        icon: ShoppingCart,
        label: "Point of Sale",
        category: "Quick Actions",
        shortcut: "Ctrl+P",
        action: "pos",
        type: "action",
      },
      {
        id: "inventory",
        icon: Package,
        label: "Inventory",
        category: "Quick Actions",
        shortcut: "Ctrl+Shift+I",
        action: "inventory",
        type: "action",
      },
      {
        id: "purchaseOrders",
        icon: Receipt,
        label: "Purchase Orders",
        category: "Quick Actions",
        shortcut: "Ctrl+Shift+P",
        action: "purchaseOrders",
        type: "action",
      },
      {
        id: "expense",
        icon: Calculator,
        label: "Record expense",
        category: "Quick Actions",
        shortcut: "Ctrl+E",
        action: "expense",
        type: "action",
      },
      {
        id: "invoice",
        icon: FileText,
        label: "Create invoice",
        category: "Quick Actions",
        shortcut: "Ctrl+I",
        action: "invoice",
        type: "action",
      },
      {
        id: "client",
        icon: Users,
        label: "Add new client",
        category: "Quick Actions",
        shortcut: "Ctrl+N",
        action: "client",
        type: "action",
      },
    ]

    if (!query.trim()) {
      return quickActions
    }

    if (!apiSearchResults) {
      return quickActions
    }

    const results: any[] = []

    // Add Products
    if (apiSearchResults.products && apiSearchResults.products.length > 0) {
      apiSearchResults.products.forEach((p: any) => {
        results.push({
          id: `product-${p.id}`,
          icon: Package,
          label: p.label,
          category: "Products",
          type: "product",
          productId: p.id,
          sku: p.sku,
          price: p.price,
          stock: p.stock,
        })
      })
    }

    // Add Customers
    if (apiSearchResults.customers && apiSearchResults.customers.length > 0) {
      apiSearchResults.customers.forEach((c: any) => {
        results.push({
          id: `customer-${c.id}`,
          icon: Building2,
          label: c.label,
          category: "Customers",
          type: "customer",
          customerId: c.id,
          email: c.email,
          phone: c.phone,
        })
      })
    }

    // Add Invoices
    if (apiSearchResults.invoices && apiSearchResults.invoices.length > 0) {
      apiSearchResults.invoices.forEach((inv: any) => {
        results.push({
          id: `invoice-${inv.id}`,
          icon: FileText,
          label: inv.label,
          category: "Invoices",
          type: "invoice",
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber,
          total: inv.total,
          status: inv.status,
        })
      })
    }

    // Add Expenses
    if (apiSearchResults.expenses && apiSearchResults.expenses.length > 0) {
      apiSearchResults.expenses.forEach((exp: any) => {
        results.push({
          id: `expense-${exp.id}`,
          icon: Calculator,
          label: exp.label,
          category: "Expenses",
          type: "expense",
          expenseId: exp.id,
          amount: exp.amount,
          date: exp.date,
        })
      })
    }

    // Add Purchase Orders
    if (apiSearchResults.purchaseOrders && apiSearchResults.purchaseOrders.length > 0) {
      apiSearchResults.purchaseOrders.forEach((po: any) => {
        results.push({
          id: `po-${po.id}`,
          icon: ShoppingCart,
          label: po.label,
          category: "Purchase Orders",
          type: "purchaseOrder",
          purchaseOrderId: po.id,
          poNumber: po.poNumber,
          total: po.total,
        })
      })
    }

    // Add Bills
    if (apiSearchResults.bills && apiSearchResults.bills.length > 0) {
      apiSearchResults.bills.forEach((bill: any) => {
        results.push({
          id: `bill-${bill.id}`,
          icon: Receipt,
          label: bill.label,
          category: "Bills",
          type: "bill",
          billId: bill.id,
          billNumber: bill.billNumber,
          total: bill.total,
        })
      })
    }

    // Add Vendors
    if (apiSearchResults.vendors && apiSearchResults.vendors.length > 0) {
      apiSearchResults.vendors.forEach((v: any) => {
        results.push({
          id: `vendor-${v.id}`,
          icon: Building2,
          label: v.label,
          category: "Vendors",
          type: "vendor",
          vendorId: v.id,
          email: v.email,
          phone: v.phone,
        })
      })
    }

    // Apply filters
    let filteredResults = results
    if (activeFilters.category.length > 0) {
      filteredResults = filteredResults.filter(item => 
        activeFilters.category.includes(item.category)
      )
    }

    return [...quickActions, ...filteredResults].slice(0, 20)
  }, [apiSearchResults, query, activeFilters])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs or textareas (unless it's the search input)
      const target = e.target as HTMLElement
      if (!target) return
      
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
      const isContentEditable = target.isContentEditable
      const isSearchInput = target.closest('[role="combobox"]') || target.closest('[data-command-input]') || target.closest('[cmdk-input]')
      
      // Allow shortcuts in search input, but block in other inputs
      if ((isInput || isContentEditable) && !isSearchInput) {
        return
      }

      // Only handle Ctrl/Cmd combinations
      if (!(e.ctrlKey || e.metaKey)) {
        return
      }

      // Open search with Ctrl+K or Cmd+K
      if (e.key === "k" || e.key === "K") {
        e.preventDefault()
        e.stopPropagation()
        setOpen(true)
        return
      }
      
      // Quick actions shortcuts - work globally
      if (!e.shiftKey && !e.altKey) {
        if (e.key === "e" || e.key === "E") {
          e.preventDefault()
          e.stopPropagation()
          setOpen(false) // Close search if open
          navigate('/dashboard/expenses?action=create')
        } else if (e.key === "i" || e.key === "I") {
          e.preventDefault()
          e.stopPropagation()
          setOpen(false) // Close search if open
          navigate('/dashboard/pos')
        } else if (e.key === "n" || e.key === "N") {
          // Prevent Ctrl+N (browser new window) and use it for our shortcut
          // On Mac, Cmd+N is less likely to conflict
          e.preventDefault()
          e.stopPropagation()
          setOpen(false) // Close search if open
          navigate('/dashboard/sales?action=createCustomer')
        }
      }
    }

    // Use capture phase to catch events early
    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [navigate])

  const handleSelect = (item: any, action?: string) => {
    setValue(item.label || item.id)
    setOpen(false)

    // Add to recent searches
    setRecentSearches((prev) => {
      const searchTerm = item.label || item.id
      const updated = [searchTerm, ...prev.filter((item) => item !== searchTerm)]
      return updated.slice(0, 5)
    })

    // Handle navigation based on type
    if (item.type === 'product' && item.productId) {
      navigate(`/dashboard/inventory?productId=${item.productId}`)
    } else if (item.type === 'customer' && item.customerId) {
      navigate(`/dashboard/sales?customerId=${item.customerId}`)
    } else if (item.type === 'invoice' && item.invoiceId) {
      navigate(`/dashboard/sales?invoiceId=${item.invoiceId}`)
    } else if (item.type === 'expense' && item.expenseId) {
      navigate(`/dashboard/expenses?expenseId=${item.expenseId}`)
    } else if (item.type === 'purchaseOrder' && item.purchaseOrderId) {
      navigate(`/dashboard/purchase-orders?poId=${item.purchaseOrderId}`)
    } else if (item.type === 'bill' && item.billId) {
      navigate(`/dashboard/enhanced-accounts-payable?billId=${item.billId}`)
    } else if (item.type === 'vendor' && item.vendorId) {
      navigate(`/dashboard/vendors?vendorId=${item.vendorId}`)
    } else if (item.type === 'hotel') {
      navigate('/dashboard/hotel')
    } else if (item.type === 'inventory') {
      navigate('/dashboard/inventory')
    } else if (item.type === 'pos') {
      navigate('/dashboard/pos')
    } else if (item.type === 'purchaseOrders') {
      navigate('/dashboard/purchase-orders')
    } else if (action === 'expense') {
      navigate('/dashboard/expenses?action=create')
    } else if (action === 'invoice') {
      navigate('/dashboard/pos')
    } else if (action === 'client') {
      navigate('/dashboard/sales?action=createCustomer')
    } else if (action === 'hotel') {
      navigate('/dashboard/hotel')
    } else if (action === 'inventory') {
      navigate('/dashboard/inventory')
    } else if (action === 'pos') {
      navigate('/dashboard/pos')
    } else if (action === 'purchaseOrders') {
      navigate('/dashboard/purchase-orders')
    }
  }

  const addFilter = (type: keyof SearchFilters, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [type]: Array.isArray(prev[type]) ? [...(prev[type] as string[]), value] : value,
    }))
  }

  const removeFilter = (type: keyof SearchFilters, value?: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [type]: Array.isArray(prev[type]) ? (prev[type] as string[]).filter((v) => v !== value) : "",
    }))
  }

  const clearAllFilters = () => {
    setActiveFilters({
      category: [],
      dateRange: "",
      amountRange: "",
      client: [],
    })
  }

  const saveCurrentFilters = () => {
    const name = `Filter ${savedFilters.length + 1}`
    setSavedFilters((prev) => [...prev, { name, filters: activeFilters }])
  }

  const hasActiveFilters = Object.values(activeFilters).some((filter) =>
    Array.isArray(filter) ? filter.length > 0 : filter !== "",
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Global search - Press Ctrl+K to open"
          data-search-input
          className="w-80 justify-start text-muted-foreground hover:text-foreground hover:bg-muted hover:border-border relative"
          onFocus={() => setFocusedElement("global-search")}
          onBlur={() => setFocusedElement(null)}
        >
          <Search className="mr-2 h-4 w-4" />
          <TranslatedText text="Search anything..." /> (Ctrl+K)
          {hasActiveFilters && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {Object.values(activeFilters).flat().filter(Boolean).length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b border-border">
            <CommandInput
              placeholder={searchPlaceholder}
              aria-label="Search input"
              className="flex-1 focus:ring-0 focus:border-transparent border-0"
              value={query}
              onValueChange={setQuery}
            />
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} className="mr-2 gap-1">
              <Filter className="w-4 h-4" />
              <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </Button>
          </div>

          {showFilters && (
            <div className="p-4 border-b border-border bg-muted/20">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Categories</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {["Products", "Customers", "Invoices", "Expenses", "Purchase Orders", "Bills", "Vendors", "Quick Actions"].map((cat) => (
                      <Button
                        key={cat}
                        variant={activeFilters.category.includes(cat) ? "default" : "outline"}
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() =>
                          activeFilters.category.includes(cat)
                            ? removeFilter("category", cat)
                            : addFilter("category", cat)
                        }
                      >
                        {cat}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Amount Range</label>
                  <div className="flex gap-1 mt-1">
                    {["0-100", "100-500", "500-1000", "1000+"].map((range) => (
                      <Button
                        key={range}
                        variant={activeFilters.amountRange === range ? "default" : "outline"}
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() =>
                          activeFilters.amountRange === range
                            ? removeFilter("amountRange")
                            : setActiveFilters((prev) => ({ ...prev, amountRange: range }))
                        }
                      >
                        ${range}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-6 text-xs bg-transparent"
                    >
                      Clear All
                    </Button>
                  )}
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={saveCurrentFilters}
                      className="h-6 text-xs bg-transparent"
                    >
                      Save Filter
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="p-2 border-b border-border">
              <div className="flex flex-wrap gap-1">
                {activeFilters.category.map((cat) => (
                  <Badge key={cat} variant="secondary" className="text-xs gap-1">
                    {cat}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFilter("category", cat)} />
                  </Badge>
                ))}
                {activeFilters.amountRange && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    ${activeFilters.amountRange}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFilter("amountRange")} />
                  </Badge>
                )}
              </div>
            </div>
          )}

          <CommandList>
            {isSearching && query.trim() && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <TranslatedText text="Searching..." />
              </div>
            )}
            <CommandEmpty>
              <div className="py-6 text-center text-sm text-muted-foreground">
                {query.trim() ? (
                  <>
                    No results found for "{query}"
                    <div className="mt-2 text-xs">Try adjusting your search terms or filters</div>
                  </>
                ) : (
                  <TranslatedText text="Start typing to search..." />
                )}
              </div>
            </CommandEmpty>

            {savedFilters.length > 0 && query === "" && (
              <CommandGroup heading="Saved Filters">
                {savedFilters.map((filter, index) => (
                  <CommandItem
                    key={`filter-${index}`}
                    onSelect={() => setActiveFilters(filter.filters)}
                    className="flex items-center justify-between focus:bg-muted focus:text-foreground"
                  >
                    <div className="flex items-center">
                      <Filter className="mr-2 h-4 w-4 text-muted-foreground" />
                      {filter.name}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {recentSearches.length > 0 && query === "" && (
              <CommandGroup heading="Recent Searches">
                {recentSearches.map((search, index) => (
                  <CommandItem
                    key={`recent-${index}`}
                    value={search}
                    onSelect={() => {
                      setQuery(search)
                      // handleSelect expects an item object, but for recent searches we just set the query
                    }}
                    className="flex items-center justify-between focus:bg-muted focus:text-foreground"
                  >
                    <div className="flex items-center">
                      <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                      {search}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {Object.entries(
              searchResults.reduce(
                (acc, item) => {
                  if (!acc[item.category]) acc[item.category] = []
                  acc[item.category].push(item)
                  return acc
                },
                {} as Record<string, any[]>,
              ),
            ).map(([category, items]) => (
              <CommandGroup key={category} heading={category}>
                {(items as any[]).map((item: any) => (
                  <CommandItem
                    key={item.id}
                    value={item.label}
                    onSelect={() => handleSelect(item, item.action)}
                    className="flex items-center justify-between aria-selected:bg-primary aria-selected:text-white focus:bg-primary focus:text-white cursor-pointer group"
                    role="option"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <item.icon className="mr-2 h-4 w-4 text-muted-foreground group-aria-selected:text-white group-focus:text-white flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {item.customer && (
                        <Badge variant="outline" className="text-xs">
                          {item.customer}
                        </Badge>
                      )}
                      {item.vendor && (
                        <Badge variant="outline" className="text-xs">
                          {item.vendor}
                        </Badge>
                      )}
                      {item.sku && (
                        <Badge variant="outline" className="text-xs">
                          {item.sku}
                        </Badge>
                      )}
                      {item.shortcut && (
                        <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
                          {item.shortcut}
                        </kbd>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
